import { useCallback, useEffect, useState } from "react";

import { UsersApiHelperForMember } from "api/ApiHelper";
import dayjs from "dayjs";

/**
 * Custom hook to fetch enriched payment data for ALL equipment loans
 * API returns data with equipment and debt schedule details already joined
 * @param {Object} globalState - Global state object containing producer_token and crop_year
 * @returns {Object} Hook return object with payments data and loading state
 */
export default function useAllEquipmentPayments(globalState) {
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const api = UsersApiHelperForMember;

  /**
   * Load ALL amortization payment records for the producer
   * API returns enriched data with equipment and debt schedule details
   */
  const loadPayments = useCallback(async () => {
    if (!globalState?.producer_token || !globalState?.crop_year) {
      setPayments([]);
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        producer_token: globalState.producer_token,
        is_active: true,
        year: globalState.crop_year,
      };

      // Fetch amortization data with enriched fields from API
      const amortizationResponse = await api.callPost("DebtScheduleAmortization/search", payload, globalState);

      if (amortizationResponse && Array.isArray(amortizationResponse)) {
        // Sort by payment_date ASC
        const sortedResponse = [...amortizationResponse].sort((a, b) => {
          const dateA = dayjs(a.payment_date);
          const dateB = dayjs(b.payment_date);
          return dateA.isBefore(dateB) ? -1 : 1;
        });
        setPayments(sortedResponse);
      } else {
        setPayments([]);
      }
    } catch (error) {
      console.error("Error loading payment records", error);
      setPayments([]);
    } finally {
      setIsLoading(false);
    }
  }, [globalState?.producer_token, globalState?.crop_year]);

  /**
   * Load payments on mount and when dependencies change
   */
  useEffect(() => {
    if (globalState?.producer_token && globalState?.crop_year) {
      loadPayments();
    }
  }, [loadPayments, globalState?.producer_token, globalState?.crop_year]);

  return {
    payments,
    isLoading,
    reloadPayments: loadPayments,
  };
}
